import React, { useContext, lazy, Suspense } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { AnimatePresence } from 'framer-motion';
import { ThemeContext } from './context/ThemeContext';

// Import only critical components that are needed immediately
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';

// Lazy load all page components for code splitting
const Home = lazy(() => import('./pages/Home'));
const OurMission = lazy(() => import('./pages/our-mission'));
const Work = lazy(() => import('./pages/Work'));
const Contact = lazy(() => import('./pages/Contact'));
const CurrentTrials = lazy(() => import('./pages/CurrentTrials'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const PrivacyHIPAA = lazy(() => import('./pages/PrivacyHIPAA'));
const TermsOfUse = lazy(() => import('./pages/TermsOfUse'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const AccessibilityPolicy = lazy(() => import('./pages/AccessibilityPolicy'));
const ClinicalStudyDisclaimers = lazy(() => import('./pages/ClinicalStudyDisclaimers'));
const PatientRights = lazy(() => import('./pages/PatientRights'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const Sitemap = lazy(() => import('./pages/Sitemap'));
const ThankYou = lazy(() => import('./pages/ThankYou'));
const UlcerativeColitisTrial = lazy(() => import('./pages/trials/UlcerativeColitisTrial'));
const DiabetesTrial = lazy(() => import('./pages/trials/DiabetesTrial'));
const AsthmaTrial = lazy(() => import('./pages/trials/AsthmaTrial'));
const InfertilityTrial = lazy(() => import('./pages/trials/InfertilityTrial'));
const NotFound = lazy(() => import('./pages/NotFound'));

const titleByPath = {
  '/': 'Smart Medical Research | Trusted Clinical Trials in Jackson Heights, NYC',
  '/our-mission': 'Our Mission | Smart Medical Research',
  '/about-us': 'Our Mission | Smart Medical Research',
  '/work': 'Clinical Research Areas & Therapeutic Expertise | Smart Medical Research',
  '/contact': 'Contact Smart Medical Research | Clinical Trial Inquiries NYC',
  '/current-trials': 'Current Clinical Trials | Smart Medical Research',
  '/male-infertility': 'Male Infertility & Low Testosterone Study | Smart Medical Research',
  '/ulcerative-colitis': 'Ulcerative Colitis & Crohn’s Clinical Trial | Smart Medical Research',
  '/diabetes': 'Type 1 Diabetes Clinical Trial | Smart Medical Research',
  '/asthma': 'Severe Asthma Clinical Trial | Smart Medical Research',
  '/privacy-policy': 'Privacy Policy | Smart Medical Research',
  '/privacy-hipaa': 'Privacy & HIPAA Notice | Smart Medical Research',
  '/terms-of-use': 'Terms of Use | Smart Medical Research',
  '/cookie-policy': 'Cookie Policy | Smart Medical Research',
  '/accessibility-policy': 'Accessibility Policy | Smart Medical Research',
  '/clinical-study-disclaimers': 'Clinical Study Disclaimers | Smart Medical Research',
  '/patient-rights': 'Patient Rights | Smart Medical Research',
  '/sitemap': 'Sitemap | Smart Medical Research',
  '/thank-you': 'Thank You | Smart Medical Research'
};

// Optimized minimal loading component
const PageLoader = React.memo(() => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-blue-50">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-600 font-poppins">Loading...</p>
    </div>
  </div>
));

PageLoader.displayName = 'PageLoader';

function App() {
  const location = useLocation();
  const { theme } = useContext(ThemeContext);
  const canonicalPath = location.pathname === '/'
    ? ''
    : location.pathname.replace(/\/+$/, '');
  const canonicalUrl = `https://smartmedicalresearch.com${canonicalPath}`;
  const pageTitle = titleByPath[canonicalPath] || 'Smart Medical Research';

  return (
    <HelmetProvider>
      <div className={`App ${theme}`}>
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content="Pioneering clinical trials for a healthier future." />
          <link rel="canonical" href={canonicalUrl} />
        </Helmet>
        
        <Header />
        
        <main>
          <Suspense fallback={<PageLoader />}>
            <AnimatePresence mode='wait'>
              <Routes location={location} key={location.pathname}>
                <Route path="/" element={<Home />} />
                <Route path="/our-mission" element={<OurMission />} />
                <Route path="/about-us" element={<Navigate to="/our-mission" replace />} />
                <Route path="/work" element={<Work />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/current-trials" element={<CurrentTrials />} />
                <Route path="/male-infertility" element={<InfertilityTrial />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/privacy-hipaa" element={<PrivacyHIPAA />} />
                <Route path="/terms-of-use" element={<TermsOfUse />} />
                <Route path="/cookie-policy" element={<CookiePolicy />} />
                <Route path="/accessibility-policy" element={<AccessibilityPolicy />} />
                <Route path="/clinical-study-disclaimers" element={<ClinicalStudyDisclaimers />} />
                <Route path="/patient-rights" element={<PatientRights />} />
                <Route path="/blog/:id" element={<BlogPost />} />
                <Route path="/sitemap" element={<Sitemap />} />
                <Route path="/thank-you" element={<ThankYou />} />
                <Route path="/ulcerative-colitis" element={<UlcerativeColitisTrial />} />
                <Route path="/diabetes" element={<DiabetesTrial />} />
                <Route path="/asthma" element={<AsthmaTrial />} />
                
                {/* Catch-all 404 route - renders NotFound directly to preserve the URL */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </AnimatePresence>
          </Suspense>
        </main>
        
        <Footer />
        
        <ScrollToTop />
      </div>
    </HelmetProvider>
  );
}

export default App;